/**
 * TagsWithOverflow - Renders tags with dynamic +N overflow based on available width.
 *
 * Two modes:
 * 1. **Prop-driven** (columnWidth provided): Pure computation via useMemo.
 *    Zero observers, zero state during resize. Used inside table cells where
 *    TanStack column.getSize() supplies the width.
 * 2. **Self-measuring** (no columnWidth): Falls back to a single ResizeObserver.
 *    Used outside tables or any generic container.
 */

import {
  useRef,
  useEffect,
  useState,
  useMemo,
  useLayoutEffect,
  memo,
  type MouseEvent,
  type ReactNode,
} from 'react';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { colors } from 'styles/theme/colors';

export type TagWithLabel = {
  id: number | string;
  label: string;
  /** When set, renders a status dot before the label (e.g. certificate expiry). */
  dotColor?: string;
  /** Tooltip shown on the status dot. */
  dotTooltip?: string;
  /** Makes the tag appear clickable and fires on click. */
  onClick?: () => void;
  /** When set, renders a × button inside the tag. */
  onDelete?: () => void;
};

export type TagsWithOverflowProps = {
  values: TagWithLabel[];
  /** Column width from TanStack table. When provided, no ResizeObserver is used. */
  columnWidth?: number;
  /** Horizontal cell padding (left + right) subtracted from columnWidth. Default: 20. */
  cellPadding?: number;
  /** Custom renderer for the overflow indicator. Receives the hidden tags; defaults to the built-in +N chip. */
  renderOverflow?: (hiddenTags: TagWithLabel[]) => ReactNode;
};

// ── Constants ────────────────────────────────────────────────────────

const GAP = 4;
const MIN_W = 40;
const MIN_RATIO = 0.45;
const OVERFLOW_W = 40;
const DEFAULT_CELL_H_PAD = 20;

// ── Layout types & computation (pure, no DOM) ────────────────────────

type VTag = { idx: number; maxW: number | null; trunc: boolean };
type Layout = { vis: VTag[]; overflow: number };

const EMPTY: Layout = { vis: [], overflow: 0 };

function fit(cw: number, nw: number[], reserve: boolean): Layout {
  const vis: VTag[] = [];
  let rem = reserve ? cw - OVERFLOW_W - GAP : cw;
  for (let i = 0; i < nw.length; i++) {
    const nat = nw[i];
    const min = Math.max(MIN_W, nat * MIN_RATIO);
    const gap = vis.length > 0 ? GAP : 0;
    const avail = rem - gap;
    if (avail >= nat) {
      vis.push({ idx: i, maxW: null, trunc: false });
      rem -= nat + gap;
    } else if (avail >= min) {
      vis.push({ idx: i, maxW: avail, trunc: true });
      rem -= avail + gap;
    } else {
      break;
    }
  }
  return { vis, overflow: nw.length - vis.length };
}

function calc(cw: number, nw: number[]): Layout {
  if (!nw.length || cw <= 0) return EMPTY;
  const r = fit(cw, nw, false);
  if (r.overflow === 0) return r;
  const r2 = fit(cw, nw, true);
  // Never collapse to zero visible tags: a lone (or first) value rendered as a
  // bare "+1" with nothing beside it is never useful. Always show at least the
  // first tag, truncated to the available width, and overflow the rest.
  if (r2.vis.length === 0) {
    const reserveOverflow = nw.length > 1 ? OVERFLOW_W + GAP : 0;
    const maxW = Math.max(MIN_W, cw - reserveOverflow);
    return { vis: [{ idx: 0, maxW, trunc: true }], overflow: nw.length - 1 };
  }
  return r2;
}

// ── Estimate tag natural width from label (avoids hidden DOM measurement) ──

// Tag styling: padding 2×8px + border 2×1px + text at 0.75rem (12px).
const TAG_CHROME = 8 + 8 + 1 + 1;
const AVG_CHAR_W = 7.2;
const DOT_CHROME = 6 + 4;
const DELETE_CHROME = 4 + 14; // gap + × button

function estimateNaturalWidth(v: TagWithLabel): number {
  const base = Math.ceil((v?.label ?? '').length * AVG_CHAR_W) + TAG_CHROME;
  const dot = v?.dotColor ? DOT_CHROME : 0;
  const del = v?.onDelete ? DELETE_CHROME : 0;
  return base + dot + del;
}

// ── Stable identity key for values array ─────────────────────────────

function valuesKey(values: TagWithLabel[]): string {
  return values.map(v => `${v?.id}:${v?.label}:${v?.dotColor ?? ''}`).join('|');
}

// ── Styled Components ────────────────────────────────────────────────

const OverflowContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  min-width: 0;
`;

const MeasureRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  display: flex;
  gap: ${GAP}px;
  height: 0;
  overflow: hidden;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: ${GAP}px;
  align-items: center;
`;

const TagBase = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey[200]};
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${colors.grey[700]};
  white-space: nowrap;
  overflow: hidden;
  flex-shrink: 0;
  min-width: 0;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
  ${p => p.$clickable && `&:hover { border-color: ${colors.grey[400]}; }`}
`;

const TagLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${p => p.$color};
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  line-height: 1;
  color: ${colors.grey[400]};
  flex-shrink: 0;

  &:hover {
    color: ${colors.grey[700]};
  }
`;

const TruncTag = styled(TagBase)`
  flex-shrink: 1;
`;

const OverflowTag = styled(TagBase)`
  cursor: default;
`;

const MeasureTag = styled(TagBase)``;

const TagContent = ({
  label,
  dotColor,
  dotTooltip,
  onDelete,
  labelTooltip,
}: Pick<TagWithLabel, 'label' | 'dotColor' | 'dotTooltip' | 'onDelete'> & {
  labelTooltip?: string;
}) => (
  <>
    {dotColor ? (
      dotTooltip ? (
        <Tooltip title={dotTooltip} arrow placement="top">
          <StatusDot $color={dotColor} />
        </Tooltip>
      ) : (
        <StatusDot $color={dotColor} />
      )
    ) : null}
    {labelTooltip ? (
      <Tooltip title={labelTooltip} arrow placement="top">
        <TagLabel>{label}</TagLabel>
      </Tooltip>
    ) : (
      <TagLabel>{label}</TagLabel>
    )}
    {onDelete ? (
      <DeleteBtn
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="remove"
        tabIndex={-1}
      >
        ×
      </DeleteBtn>
    ) : null}
  </>
);

// ── Render the computed layout ───────────────────────────────────────

function renderLayout(
  values: TagWithLabel[],
  layout: Layout,
  suppressTitle?: boolean,
  renderOverflow?: (hiddenTags: TagWithLabel[]) => ReactNode
) {
  const { vis, overflow } = layout;
  const hiddenTags = overflow > 0 ? values.slice(vis.length) : [];
  const hiddenLabels = hiddenTags.map(v => v?.label).join(', ');
  return (
    <TagRow>
      {vis.map(vt => {
        const v = values[vt.idx];
        if (!v) return null;
        const clickable = Boolean(v?.onClick);
        const handleClick = clickable
          ? (e: MouseEvent<HTMLSpanElement>) => {
              e.stopPropagation();
              v?.onClick?.();
            }
          : undefined;

        if (vt.maxW != null) {
          // When the tag has a dot tooltip, move the label tooltip onto the TagLabel
          // only — otherwise hovering the dot would trigger both tooltips at once.
          if (v?.dotTooltip) {
            return (
              <TruncTag
                key={String(v?.id)}
                style={{ maxWidth: vt.maxW }}
                $clickable={clickable}
                onClick={handleClick}
              >
                <TagContent {...v} labelTooltip={v?.label} />
              </TruncTag>
            );
          }

          const tag = (
            <TruncTag
              key={String(v?.id)}
              style={{ maxWidth: vt.maxW }}
              $clickable={clickable}
              onClick={handleClick}
            >
              <TagContent {...v} />
            </TruncTag>
          );
          return suppressTitle ? (
            <Tooltip key={String(v?.id)} title={v?.label} arrow placement="top">
              {tag}
            </Tooltip>
          ) : (
            <TruncTag
              key={String(v?.id)}
              style={{ maxWidth: vt.maxW }}
              $clickable={clickable}
              onClick={handleClick}
              title={v?.label}
            >
              <TagContent {...v} />
            </TruncTag>
          );
        }
        return (
          <TagBase key={String(v?.id)} $clickable={clickable} onClick={handleClick}>
            <TagContent {...v} />
          </TagBase>
        );
      })}
      {overflow > 0 && (
        <Tooltip title={renderOverflow?.(hiddenTags) ?? hiddenLabels} arrow placement="top">
          <OverflowTag title={suppressTitle ? undefined : hiddenLabels}>+{overflow}</OverflowTag>
        </Tooltip>
      )}
    </TagRow>
  );
}

// ── Prop-driven component (used in table cells) ──────────────────────

const PropDriven = memo(function PropDriven({
  values,
  columnWidth,
  cellPadding = DEFAULT_CELL_H_PAD,
  renderOverflow,
}: {
  values: TagWithLabel[];
  columnWidth: number;
  cellPadding?: number;
  renderOverflow?: (hiddenTags: TagWithLabel[]) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<number[] | null>(null);
  const [measured, setMeasured] = useState(false);

  const stableKey = useMemo(() => valuesKey(values), [values]);

  useLayoutEffect(() => {
    measuredRef.current = null;
    setMeasured(false);
  }, [stableKey]);

  useLayoutEffect(() => {
    if (measured) return;
    const el = containerRef.current;
    if (!el) return;
    const measureEl = el.firstElementChild;
    if (!measureEl) return;
    const widths: number[] = [];
    const kids = measureEl.children;
    for (let i = 0; i < kids.length; i++) {
      const child = kids[i];
      if (child instanceof HTMLElement) widths.push(child.offsetWidth);
    }
    measuredRef.current = widths;
    setMeasured(true);
  }, [measured]);

  const nw = useMemo(() => {
    if (measuredRef.current) return measuredRef.current;
    return values.map(v => estimateNaturalWidth(v));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `measured` flag triggers recalc after DOM measurement
  }, [values, measured]);

  const availableWidth = Math.max(0, columnWidth - cellPadding);
  const layout = useMemo(() => calc(availableWidth, nw), [availableWidth, nw]);

  return (
    <OverflowContainer ref={containerRef}>
      {!measured && (
        <MeasureRow aria-hidden>
          {values.map(v => (
            <MeasureTag key={String(v?.id)}>
              <TagContent {...v} />
            </MeasureTag>
          ))}
        </MeasureRow>
      )}
      {renderLayout(values, layout, true, renderOverflow)}
    </OverflowContainer>
  );
});

// ── Self-measuring component (fallback for non-table contexts) ───────

const SelfMeasuring = memo(function SelfMeasuring({
  values,
  renderOverflow,
}: {
  values: TagWithLabel[];
  renderOverflow?: (hiddenTags: TagWithLabel[]) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const nwRef = useRef<number[] | null>(null);
  const layoutRef = useRef<Layout>(EMPTY);
  const rafRef = useRef(0);
  const [, setTick] = useState(0);

  const recompute = () => {
    const el = containerRef.current;
    const nw = nwRef.current;
    if (!el || !nw) return;
    const next = calc(el.clientWidth, nw);
    if (
      layoutRef.current.overflow !== next.overflow ||
      layoutRef.current.vis.length !== next.vis.length
    ) {
      layoutRef.current = next;
      setTick(t => t + 1);
    } else {
      for (let i = 0; i < next.vis.length; i++) {
        if (
          layoutRef.current.vis[i].idx !== next.vis[i].idx ||
          layoutRef.current.vis[i].maxW !== next.vis[i].maxW
        ) {
          layoutRef.current = next;
          setTick(t => t + 1);
          return;
        }
      }
    }
  };

  useEffect(() => {
    nwRef.current = null;
    layoutRef.current = EMPTY;
    const id = requestAnimationFrame(() => {
      const m = measureRef.current;
      if (!m) return;
      const widths: number[] = [];
      for (let i = 0; i < m.children.length; i++) {
        const child = m.children[i];
        if (child instanceof HTMLElement) widths.push(child.offsetWidth);
      }
      nwRef.current = widths;
      recompute();
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute is stable, only re-run when values change
  }, [values]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(recompute);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RO setup runs once on mount; recompute is stable
  }, []);

  return (
    <OverflowContainer ref={containerRef}>
      <MeasureRow ref={measureRef} aria-hidden>
        {values.map(v => (
          <MeasureTag key={String(v?.id)}>
            <TagContent label={v?.label} dotColor={v?.dotColor} />
          </MeasureTag>
        ))}
      </MeasureRow>
      {renderLayout(values, layoutRef.current, undefined, renderOverflow)}
    </OverflowContainer>
  );
});

// ── Public component ─────────────────────────────────────────────────

const TagsWithOverflowInner = ({
  values,
  columnWidth,
  cellPadding,
  renderOverflow,
}: TagsWithOverflowProps) => {
  if (values.length === 0) return null;

  if (columnWidth != null && columnWidth > 0) {
    return (
      <PropDriven
        values={values}
        columnWidth={columnWidth}
        cellPadding={cellPadding}
        renderOverflow={renderOverflow}
      />
    );
  }

  return <SelfMeasuring values={values} renderOverflow={renderOverflow} />;
};

export const TagsWithOverflow = memo(TagsWithOverflowInner);
